/**
 * useGeometryPreview hook
 *
 * Provides a real-time geometry outline preview during node drag operations.
 * Creates a separate VectorLayer overlay with a dashed line style that shows
 * the full geometry shape updating as the node is dragged.
 *
 * **Validates: Requirement 17.4** – WHEN a Registered_User modifies a geometry,
 * THE Geometry_Editor SHALL display real-time preview of changes.
 */

import { useEffect, useRef, useCallback } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OLFeature from 'ol/Feature';
import OLLineString from 'ol/geom/LineString';
import OLPolygon from 'ol/geom/Polygon';
import { fromLonLat } from 'ol/proj';
import { Style, Stroke, Fill } from 'ol/style';
import type OLMap from 'ol/Map';
import { moveNode, type Geometry, type Coordinate } from '../services/GeometryEditor';

/**
 * Style configuration for the geometry preview overlay.
 */
export interface PreviewStyleConfig {
  /** Stroke color for the preview outline. Default: '#1976d2' (blue) */
  strokeColor?: string;
  /** Stroke width for the preview outline. Default: 2 */
  strokeWidth?: number;
  /** Dash pattern for the preview outline. Default: [8, 4] */
  lineDash?: number[];
  /** Fill color for polygon preview (semi-transparent). Default: 'rgba(25, 118, 210, 0.1)' */
  fillColor?: string;
}

const DEFAULT_PREVIEW_STYLE: Required<PreviewStyleConfig> = {
  strokeColor: '#1976d2',
  strokeWidth: 2,
  lineDash: [8, 4],
  fillColor: 'rgba(25, 118, 210, 0.1)',
};

/**
 * Options for the useGeometryPreview hook.
 */
export interface UseGeometryPreviewOptions {
  /** Reference to the OpenLayers map instance */
  mapRef: React.RefObject<OLMap | null>;
  /** Optional style configuration for the preview */
  styleConfig?: PreviewStyleConfig;
}

/**
 * Return type for the useGeometryPreview hook.
 * Provides imperative methods to show/update/hide the preview.
 */
export interface GeometryPreviewControls {
  /**
   * Shows or updates the geometry preview with the node moved to a new position.
   * @param geometry - The original geometry being edited
   * @param nodeIndex - The index of the node being dragged
   * @param newPosition - The current position of the dragged node [lon, lat]
   */
  showPreview: (geometry: Geometry, nodeIndex: number, newPosition: Coordinate) => void;
  /**
   * Removes the geometry preview from the map.
   */
  hidePreview: () => void;
}

/**
 * Converts a GeoJSON-like geometry to an OpenLayers geometry feature for preview.
 * Supports LineString and Polygon types.
 */
export function createPreviewFeature(geometry: Geometry): OLFeature | null {
  switch (geometry.type) {
    case 'LineString': {
      const coords = (geometry.coordinates as Coordinate[]).map((c) => fromLonLat(c));
      const olGeom = new OLLineString(coords);
      return new OLFeature({ geometry: olGeom });
    }
    case 'Polygon': {
      const rings = (geometry.coordinates as Coordinate[][]).map((ring) =>
        ring.map((c) => fromLonLat(c))
      );
      const olGeom = new OLPolygon(rings);
      return new OLFeature({ geometry: olGeom });
    }
    case 'MultiLineString': {
      // For MultiLineString, show each line as a separate feature
      // but for simplicity, flatten into individual LineString features
      // We'll return the first one; a more complete implementation could return multiple
      const lines = geometry.coordinates as Coordinate[][];
      if (lines.length === 0) return null;
      const coords = lines[0].map((c) => fromLonLat(c));
      const olGeom = new OLLineString(coords);
      return new OLFeature({ geometry: olGeom });
    }
    case 'MultiPolygon': {
      // For MultiPolygon, show the first polygon
      const polygons = geometry.coordinates as Coordinate[][][];
      if (polygons.length === 0) return null;
      const rings = polygons[0].map((ring) => ring.map((c) => fromLonLat(c)));
      const olGeom = new OLPolygon(rings);
      return new OLFeature({ geometry: olGeom });
    }
    default:
      // Point and MultiPoint don't have outline geometry to preview
      return null;
  }
}

/**
 * Creates the OL Style for the geometry preview overlay.
 */
export function createPreviewStyle(config: Required<PreviewStyleConfig>): Style {
  return new Style({
    stroke: new Stroke({
      color: config.strokeColor,
      width: config.strokeWidth,
      lineDash: config.lineDash,
    }),
    fill: new Fill({
      color: config.fillColor,
    }),
  });
}

/**
 * useGeometryPreview
 *
 * A React hook that manages a geometry preview overlay during node drag operations.
 * It creates a VectorLayer with a dashed line style that shows the full geometry
 * outline updating in real-time as the user drags a node.
 *
 * Usage:
 * ```tsx
 * const { showPreview, hidePreview } = useGeometryPreview({ mapRef });
 *
 * // During drag pointermove:
 * showPreview(currentGeometry, draggedNodeIndex, newPosition);
 *
 * // On drag end:
 * hidePreview();
 * ```
 */
export function useGeometryPreview({
  mapRef,
  styleConfig,
}: UseGeometryPreviewOptions): GeometryPreviewControls {
  const overlayLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const overlaySourceRef = useRef<VectorSource | null>(null);

  // Merge user config with defaults
  const config: Required<PreviewStyleConfig> = {
    ...DEFAULT_PREVIEW_STYLE,
    ...styleConfig,
  };

  const styleRef = useRef<Style>(createPreviewStyle(config));

  // Update style when config changes
  useEffect(() => {
    styleRef.current = createPreviewStyle(config);
  }, [config.strokeColor, config.strokeWidth, config.fillColor, config.lineDash]);

  /**
   * Ensures the preview layer exists on the map.
   * Creates it lazily on first use.
   */
  const ensureLayer = useCallback((): VectorSource | null => {
    const map = mapRef.current;
    if (!map) return null;

    if (!overlaySourceRef.current) {
      overlaySourceRef.current = new VectorSource();
    }

    if (!overlayLayerRef.current) {
      overlayLayerRef.current = new VectorLayer({
        source: overlaySourceRef.current,
        zIndex: 9998, // Just below node handles (9999)
      });
      (overlayLayerRef.current as any).isGeometryPreviewOverlay = true;
      map.addLayer(overlayLayerRef.current);
    }

    return overlaySourceRef.current;
  }, [mapRef]);

  /**
   * Shows or updates the geometry preview with the node at a new position.
   */
  const showPreview = useCallback(
    (geometry: Geometry, nodeIndex: number, newPosition: Coordinate) => {
      const source = ensureLayer();
      if (!source) return;

      // Compute the preview geometry with the node moved
      const result = moveNode(geometry, nodeIndex, newPosition);
      if (!result.success || !result.geometry) return;

      // Create an OL feature from the preview geometry
      const previewFeature = createPreviewFeature(result.geometry);
      if (!previewFeature) return;

      // Apply the dashed preview style
      previewFeature.setStyle(styleRef.current);

      // Replace existing features with the new preview
      source.clear();
      source.addFeature(previewFeature);
    },
    [ensureLayer]
  );

  /**
   * Removes the geometry preview from the map.
   */
  const hidePreview = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (overlaySourceRef.current) {
      overlaySourceRef.current.clear();
    }

    if (overlayLayerRef.current) {
      map.removeLayer(overlayLayerRef.current);
      overlayLayerRef.current = null;
      overlaySourceRef.current = null;
    }
  }, [mapRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const map = mapRef.current;
      if (map && overlayLayerRef.current) {
        map.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
        overlaySourceRef.current = null;
      }
    };
  }, [mapRef]);

  return { showPreview, hidePreview };
}

export default useGeometryPreview;
